// src/pages/PdfResources.tsx

import React, { useState, useEffect } from 'react';
import { publicApi, type PdfProduct } from '../services/api';
import { useCart } from '../contexts/CartContext';

const PdfResources: React.FC = () => {
  const [pdfs, setPdfs] = useState<PdfProduct[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { addToCart } = useCart();

  // Load categories on mount
  useEffect(() => {
    loadCategories();
  }, []);

  // Load PDFs when category changes
  useEffect(() => {
    loadPdfs();
  }, [selectedCategory]);

  const loadCategories = async () => {
    try {
      const data = await publicApi.getCategories();
      setCategories(data);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const loadPdfs = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await publicApi.getPdfs(selectedCategory);
      setPdfs(data);
    } catch (error) {
      console.error('Failed to load PDFs:', error);
      setError('Failed to load PDF products. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (pdf: PdfProduct) => {
    addToCart({
      id: pdf.id,
      title: pdf.title,
      price: Number(pdf.price),
      category: pdf.category,
      productType: 'pdf',
    });
    // Optional: Show a toast notification or success message
    alert(`"${pdf.title}" added to cart!`);
  };

  if (loading && categories.length === 0) {
    return (
      <div className="section container" style={{ textAlign: 'center', paddingTop: '4rem' }}>
        <p>Loading PDF products...</p>
      </div>
    );
  }

  return (
    <div className="section container">
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <h1 style={{ marginBottom: '1rem' }}>PDF Resources</h1>
        <p className="text-secondary" style={{ marginBottom: '2rem' }}>
          Professional business templates, guides, and resources to help your organization succeed.
        </p>

        {/* Category Filter */}
        {categories.length > 0 && (
          <div style={{ marginBottom: '3rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
              <label style={{ fontWeight: '500', fontSize: '0.875rem', margin: 0 }}>
                Filter by Category
              </label>
              {selectedCategory !== 'all' && (
                <button
                  onClick={() => setSelectedCategory('all')}
                  style={{
                    padding: '0.25rem 0.75rem',
                    border: '1px solid #d1d5db',
                    backgroundColor: 'white',
                    color: '#6b7280',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                    fontWeight: '500',
                  }}
                >
                  ✕ Clear Filter
                </button>
              )}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              <button
                onClick={() => setSelectedCategory('all')}
                style={{
                  padding: '0.5rem 1rem',
                  border: selectedCategory === 'all' ? '2px solid var(--color-primary)' : '1px solid #d1d5db',
                  backgroundColor: selectedCategory === 'all' ? 'var(--color-primary)' : 'white',
                  color: selectedCategory === 'all' ? 'white' : '#374151',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                }}
              >
                All Categories
              </button>
              {categories.map((category, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedCategory(category)}
                  style={{
                    padding: '0.5rem 1rem',
                    border: selectedCategory === category ? '2px solid var(--color-primary)' : '1px solid #d1d5db',
                    backgroundColor: selectedCategory === category ? 'var(--color-primary)' : 'white',
                    color: selectedCategory === category ? 'white' : '#374151',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                  }}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        )}

        {error && (
          <div style={{
            padding: '1rem',
            marginBottom: '2rem',
            backgroundColor: '#fee2e2',
            color: '#991b1b',
            borderRadius: '6px',
            textAlign: 'center'
          }}>
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <p className="text-secondary">Loading PDFs...</p>
          </div>
        ) : pdfs.length === 0 ? (
          <div className="card" style={{ padding: '3rem', textAlign: 'center' }}>
            <p className="text-secondary">
              {selectedCategory === 'all'
                ? 'No PDF products available at this time. Check back soon!'
                : `No PDFs found in "${selectedCategory}" category.`
              }
            </p>
          </div>
        ) : (
          <div>
            <p className="text-secondary" style={{ marginBottom: '1.5rem', fontSize: '0.875rem' }}>
              Showing {pdfs.length} {pdfs.length === 1 ? 'product' : 'products'}
              {selectedCategory !== 'all' && ` in "${selectedCategory}"`}
            </p>
            <div className="grid grid-3" style={{ gap: '2rem' }}>
              {pdfs.map((pdf) => (
                <div key={pdf.id} className="card" style={{ padding: '1.5rem' }}>
                  {pdf.category && (
                    <span style={{
                      display: 'inline-block',
                      fontSize: '0.75rem',
                      padding: '0.25rem 0.5rem',
                      backgroundColor: '#e0e7ff',
                      color: '#4f46e5',
                      borderRadius: '4px',
                      fontWeight: '500',
                      marginBottom: '0.75rem'
                    }}>
                      {pdf.category}
                    </span>
                  )}
                  <h3 style={{ marginBottom: '1rem' }}>{pdf.title}</h3>
                  <p className="text-secondary" style={{ marginBottom: '1rem', minHeight: '3em' }}>
                    {pdf.description}
                  </p>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--color-primary)' }}>
                      ${Number(pdf.price).toFixed(2)}
                    </span>
                    <button
                      className="btn btn-primary"
                      onClick={() => handleAddToCart(pdf)}
                    >
                      Add to Cart
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ marginTop: '4rem', textAlign: 'center', padding: '2rem', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
          <h2 style={{ marginBottom: '1rem' }}>Need Custom Resources?</h2>
          <p className="text-secondary" style={{ marginBottom: '1.5rem' }}>
            We can create custom templates and guides tailored to your specific business needs.
          </p>
          <a href="/contact" className="btn btn-primary">
            Contact Us
          </a>
        </div>
      </div>
    </div>
  );
};

export default PdfResources;
